
import { Component, Vue, Watch } from "vue-property-decorator";
import { DimssaButton } from "@/components/shared/dimssa-button.vue";
import { experimentalVersion } from "@/components/shared/experimental-version.vue";
import lodash from "lodash";
import { v4 as uuidv4 } from "uuid";
import * as Models from "@gigalot/data-models";
import BreadcrumbTrail from "@/components/BreadcrumbTrail.vue";
import { homeBreadcrumbs } from "@/utils/breadcrumbs";

interface HelpbotInlineUploadFile {
  filename: string;
  mimeType?: string;
  encoding: "base64" | "utf8";
  content: string;
}

interface HelpbotBundledDocEntry {
  filename: string;
  path: string;
  title?: string;
  mimeType?: string;
  bytes?: number;
}

interface HelpbotBundledDocsManifest {
  generatedAt?: string;
  source?: string;
  fileCount?: number;
  totalBytes?: number;
  files?: HelpbotBundledDocEntry[];
}

@Component({
  components: {
    DimssaButton,
    experimentalVersion,
    BreadcrumbTrail,
  },
})
export default class Options extends Vue {
  loading = false;
  time = "";
  strategy: Models.FeedingStrategy = { typename: "FeedingStrategy", guid: uuidv4(), FeedingPlanItems: [] };
  selectedStrategy: Models.FeedingStrategyItem[] = [];
  kraalTypes: Models.KraalType[] = [];
  genderTypes: Models.GenderType[] = [];
  genderTypeSearch = "";
  genderDialog = false;
  genderDialogMode: "add" | "edit" = "add";
  genderFormValid = false;
  genderForm: Models.GenderType = { typename: "GenderType", genderType: "", guid: "", active: true };
  genderToRemove: Models.GenderType | null = null;
  removeGenderDialog = false;
  cribAdjustments = {};
  selectedCribAdjustment = [];
  rationPlan = [];
  rationPriorities = [];
  configuration = {};
  cribDeliverySequence: Models.CribDeliverySequenceItem[] = [];
  driveDeliverySequence: Models.CribDeliverySequenceItem[] = [];
  selectedCrib: Models.CribDeliverySequenceItem[] = [];
  selectedDrive: Models.CribDeliverySequenceItem[] = [];
  cribOrderSearch = "";
  driveOrderSearch = "";
  selectedRation = [];
  sessionSelection = this.strategy.FeedingPlanItems.map((fpi) => fpi.sequence);
  snack = false;
  webrtcEnabled = true;
  snackColor = "";
  snackText = "";
  errorText = "";
  errorDialog = false;
  mergeSessionDialog = false;
  newDayDialog = false;
  newDayMode: "upload" | "noUpload" | null = null;
  gigalotAdmin = false;
  helpbotVectorStoreName = "feeder-helpbot-docs";
  helpbotVectorStoreId = "";
  helpbotExpiresAfterDays: number | null = null;
  helpbotUploadFiles: File[] = [];
  helpbotUploadLoading = false;
  helpbotBundledDocsLoading = false;
  helpbotUploadError = "";
  helpbotUploadResult: any = null;
  tableKey = 0;
  get themeClass(): string {
    return this.$store.state.lightDarkMode;
  }

  get breadcrumbs() {
    return homeBreadcrumbs({ text: "Options & Settings", disabled: true });
  }
  get feedlotName(): string {
    const location = this.$store.state.user && this.$store.state.user.location ? this.$store.state.user.location : undefined;
    return location && location.description ? location.description : "";
  }
  get hasSelectedStrategy(): boolean {
    return Array.isArray(this.selectedStrategy) && this.selectedStrategy.length > 0;
  }
  get hasSelectedCrib(): boolean {
    return Array.isArray(this.selectedCrib) && this.selectedCrib.length > 0;
  }
  get hasSelectedDrive(): boolean {
    return Array.isArray(this.selectedDrive) && this.selectedDrive.length > 0;
  }
  get genderDialogTitle(): string {
    return this.genderDialogMode === "add" ? "Add gender type" : "Edit gender type";
  }
  get helpbotVectorEndpoint(): string {
    const configuredEndpoint = String(process.env.VUE_APP_HELPBOT_VECTOR_ENDPOINT || "").trim();
    if (configuredEndpoint) return configuredEndpoint;

    const helpbotEndpoint = String(process.env.VUE_APP_HELPBOT_ENDPOINT || "").trim();
    if (!helpbotEndpoint) return "";

    try {
      const endpointUrl = new URL(helpbotEndpoint);
      if (endpointUrl.hostname.startsWith("helpbot-")) {
        endpointUrl.hostname = endpointUrl.hostname.replace(/^helpbot-/, "createhelpbotvectorstore-");
        return endpointUrl.toString();
      }
    } catch {
      return "";
    }

    return "";
  }
  get helpbotBusy(): boolean {
    return this.helpbotUploadLoading || this.helpbotBundledDocsLoading;
  }
  get helpbotDocsManifestUrl(): string {
    return this.buildHelpbotPublicAssetUrl("helpbot-docs/manifest.json");
  }
  get helpbotUploadTotalBytes(): number {
    return this.helpbotUploadFiles.reduce((total, file) => total + (file?.size || 0), 0);
  }
  get helpbotUploadSizeLabel(): string {
    return this.formatBytes(this.helpbotUploadTotalBytes);
  }
  swatches = [
    ["#FF0000", "#AA0000", "#550000"],
    ["#FFFF00", "#AAAA00", "#555500"],
    ["#00FF00", "#00AA00", "#005500"],
    ["#00FFFF", "#00AAAA", "#005555"],
    ["#0000FF", "#0000AA", "#000055"],
  ];
  kraalTypeHeaders = [
    {
      text: "Type",
      value: "kraalType",
      align: "center",
      sortable: true,
      filterable: true,
      divider: true,
    },
    {
      text: "Feed",
      value: "doFeed",
      align: "center",
      sortable: true,
      filterable: true,
      divider: true,
    },
    {
      text: "Manual/Auto",
      value: "feedType",
      align: "center",
      sortable: true,
      filterable: true,
      divider: true,
    },
    {
      text: "Number of Sessions",
      value: "noOfSessions",
      align: "center",
      sortable: true,
      filterable: true,
      divider: true,
    },
  ];
  genderTypeHeaders = [
    {
      text: "Gender",
      value: "genderType",
      align: "start",
      sortable: true,
      filterable: true,
      divider: true,
    },
    {
      text: "Status",
      value: "active",
      align: "center",
      sortable: true,
      filterable: true,
      divider: true,
    },
    {
      text: "",
      value: "actions",
      align: "end",
      sortable: false,
      filterable: false,
      divider: true,
    },
  ];
  genderNameRules = [
    (value: string) => !!String(value || "").trim() || "Enter a gender name",
    (value: string) => String(value || "").trim().length <= 40 || "Use 40 characters or fewer",
  ];
  headersStrategy = [
    {
      text: "Order",
      value: "sequence",
      align: "center",
      sortable: true,
      filterable: true,
      divider: true,
    },
    {
      text: "Feed %",
      value: "percentage",
      align: "center",
      sortable: true,
      filterable: true,
      divider: true,
    },
    {
      text: "Delay Time",
      value: "startZhours",
      align: "center",
      sortable: true,
      filterable: true,
      divider: true,
    },
    {
      text: "Underfeed Tolerance %",
      value: "tolerancePercentage",
      align: "center",
      sortable: true,
      filterable: true,
      divider: true,
    },
  ];

  headersRationPlan = [
    {
      text: "Ration",
      value: "ration",
      align: "center",
      sortable: true,
      filterable: true,
      divider: true,
    },
    {
      text: "Days on Feed",
      value: "defaultDays",
      align: "center",
      sortable: true,
      filterable: true,
      divider: true,
    },
    {
      text: "Order",
      value: "order",
      align: "center",
      sortable: true,
      filterable: true,
      divider: true,
    },
    {
      text: "Colour",
      value: "colour",
      align: "center",
      sortable: true,
      filterable: true,
      divider: true,
    },
  ];

  headersCribAdjustments = [
    {
      text: "Crib Score",
      value: "score",
      align: "center",
      sortable: true,
      filterable: true,
      divider: true,
    },
    {
      text: "User Alias",
      value: "userAlias",
      align: "center",
      sortable: true,
      filterable: true,
      divider: true,
    },
    {
      text: "Auto call adjustment",
      value: "autoCallAdjustment",
      align: "center",
      sortable: true,
      filterable: true,
      divider: true,
    },
  ];

  headersCribDeliverySequence = [
    {
      text: "Kraal",
      value: "kraalId",
      align: "center",
      sortable: true,
      filterable: true,
      divider: true,
    },
    {
      text: "Order",
      value: "sequence",
      align: "center",
      sortable: true,
      filterable: true,
      divider: true,
    },
  ];

  rule100percent = [
    (v: any) => {
      if (v > 100) return "Max 100 percent";
      if (v < 0) return "Min 0 percent";
    },
  ];

  ensureCribAdjustmentDefaults() {
    const cribAdjustments = this.cribAdjustments as any;
    if (cribAdjustments && cribAdjustments.usePrecentageAdjustment === undefined) {
      this.$set(cribAdjustments, "usePrecentageAdjustment", false);
    }
  }

  ensureConfigurationDefaults() {
    const configuration = this.configuration as any;
    if (!configuration) return;

    const jobReservePercent = Number(configuration.jobReservePercent);
    if (configuration.jobReservePercent === undefined || configuration.jobReservePercent === null || Number.isNaN(jobReservePercent)) {
      this.$set(configuration, "jobReservePercent", 0);
      return;
    }

    this.$set(configuration, "jobReservePercent", jobReservePercent);
  }

  mounted() {
    this.$store.dispatch("user/addFirebaseCallback", this.getData);

    this.webrtcEnabled = this.$store.state.useP2PConn;
  }

  get moment() {
    return this.$store.state.moment;
  }

  mergeSession() {
    this.putMergeSession();
  }

  clearHelpbotUploadSelection() {
    this.helpbotUploadFiles = [];
    this.helpbotUploadError = "";
  }

  async uploadHelpbotKnowledgeFiles() {
    this.helpbotUploadError = "";
    this.helpbotUploadResult = null;

    const endpoint = this.helpbotVectorEndpoint;
    if (!endpoint) {
      this.helpbotUploadError = "No upload endpoint configured for vector store management.";
      return;
    }

    if (!this.helpbotUploadFiles.length) {
      this.helpbotUploadError = "Select at least one file to upload.";
      return;
    }

    const maxRawBytes = 20 * 1024 * 1024;
    if (this.helpbotUploadTotalBytes > maxRawBytes) {
      this.helpbotUploadError = `Selected files are too large (${this.helpbotUploadSizeLabel}). Limit is 20 MB per upload.`;
      return;
    }

    this.helpbotUploadLoading = true;
    try {
      const filesPayload: HelpbotInlineUploadFile[] = await Promise.all(
        this.helpbotUploadFiles.map(async (file) => ({
          filename: file.name,
          mimeType: file.type || undefined,
          encoding: "base64" as const,
          content: await this.readFileAsBase64(file),
        }))
      );
      await this.submitHelpbotKnowledgeUpload(filesPayload, "HelpBot vector store updated.");
      this.helpbotUploadFiles = [];
    } catch (err) {
      this.handleHelpbotUploadFailure(err);
    } finally {
      this.helpbotUploadLoading = false;
    }
  }

  async uploadBundledHelpbotDocs() {
    this.helpbotUploadError = "";
    this.helpbotUploadResult = null;

    const endpoint = this.helpbotVectorEndpoint;
    if (!endpoint) {
      this.helpbotUploadError = "No upload endpoint configured for vector store management.";
      return;
    }

    this.helpbotBundledDocsLoading = true;
    try {
      const { filesPayload, fileCount, totalBytes } = await this.loadBundledHelpbotDocsPayload();
      await this.submitHelpbotKnowledgeUpload(filesPayload, `Uploaded ${fileCount} bundled HelpBot docs (${this.formatBytes(totalBytes)}).`);
    } catch (err) {
      this.handleHelpbotUploadFailure(err);
    } finally {
      this.helpbotBundledDocsLoading = false;
    }
  }

  buildHelpbotPublicAssetUrl(relativePath: string): string {
    const baseUrl = String(process.env.BASE_URL || "/");
    const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
    const cleanPath = relativePath.replace(/^\/+/, "");
    return `${normalizedBaseUrl}${cleanPath}`;
  }

  formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  async loadBundledHelpbotDocsPayload(): Promise<{ filesPayload: HelpbotInlineUploadFile[]; fileCount: number; totalBytes: number }> {
    const manifestResponse = await fetch(this.helpbotDocsManifestUrl, {
      headers: new Headers({
        Accept: "application/json",
      }),
      cache: "no-store",
    });

    if (!manifestResponse.ok) {
      throw new Error(`Bundled docs manifest could not be loaded (${manifestResponse.status}).`);
    }

    const manifest = (await manifestResponse.json()) as HelpbotBundledDocsManifest;
    const manifestFiles = Array.isArray(manifest.files) ? manifest.files.filter((entry) => !!entry?.path) : [];
    if (!manifestFiles.length) {
      throw new Error("No bundled HelpBot docs were found.");
    }

    const totalBytes =
      typeof manifest.totalBytes === "number"
        ? manifest.totalBytes
        : manifestFiles.reduce((total, entry) => total + (typeof entry.bytes === "number" ? entry.bytes : 0), 0);

    const maxRawBytes = 20 * 1024 * 1024;
    if (totalBytes > maxRawBytes) {
      throw new Error(`Bundled docs are too large (${this.formatBytes(totalBytes)}). Limit is 20 MB per upload.`);
    }

    const filesPayload: HelpbotInlineUploadFile[] = await Promise.all(
      manifestFiles.map(async (entry) => {
        const response = await fetch(this.buildHelpbotPublicAssetUrl(entry.path), {
          headers: new Headers({
            Accept: entry.mimeType || "text/markdown, text/plain",
          }),
          cache: "no-store",
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch bundled doc: ${entry.filename}`);
        }

        return {
          filename: entry.filename,
          mimeType: entry.mimeType || "text/markdown",
          encoding: "utf8" as const,
          content: await response.text(),
        };
      })
    );

    return {
      filesPayload,
      fileCount: manifestFiles.length,
      totalBytes,
    };
  }

  async submitHelpbotKnowledgeUpload(filesPayload: HelpbotInlineUploadFile[], successMessage: string) {
    const endpoint = this.helpbotVectorEndpoint;
    if (!endpoint) {
      throw new Error("No upload endpoint configured for vector store management.");
    }

    const jwt = await this.$store.dispatch("user/getOnlineIdToken", undefined, { root: true });
    const payload: any = {
      files: filesPayload,
    };

    if (this.helpbotVectorStoreName.trim()) {
      payload.name = this.helpbotVectorStoreName.trim();
    }
    if (this.helpbotVectorStoreId.trim()) {
      payload.vectorStoreId = this.helpbotVectorStoreId.trim();
    }
    if (this.helpbotExpiresAfterDays) {
      payload.expiresAfterDays = this.helpbotExpiresAfterDays;
    }

    const response = await fetch(endpoint, {
      method: "POST",
      headers: new Headers({
        Authorization: `Bearer ${jwt}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
      body: JSON.stringify(payload),
    });

    const responseText = await response.text();
    let responseData: any = {};
    if (responseText) {
      try {
        responseData = JSON.parse(responseText);
      } catch {
        responseData = { message: responseText };
      }
    }

    if (!response.ok) {
      throw new Error(responseData?.message || responseData?.error || `Upload failed (${response.status})`);
    }

    this.helpbotUploadResult = responseData;
    if (responseData?.vectorStoreId) {
      this.helpbotVectorStoreId = String(responseData.vectorStoreId);
    }

    this.snack = true;
    this.snackColor = "success";
    this.snackText = successMessage;
  }

  handleHelpbotUploadFailure(err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    this.helpbotUploadError = message;
    this.snack = true;
    this.snackColor = "error";
    this.snackText = `Vector store upload failed: ${message}`;
  }

  readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = typeof reader.result === "string" ? reader.result : "";
        if (!result) {
          reject(new Error(`Failed to read file: ${file.name}`));
          return;
        }
        const commaIndex = result.indexOf(",");
        resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
      };
      reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
      reader.readAsDataURL(file);
    });
  }

  async getData() {
    this.loading = true;
    try {
      this.gigalotAdmin = this.$store.getters["user/isUserGigalotAdmin"]();
      console.log("getData()");

      let gql = `query($guid:String!){
        Strategy(guid:$guid) {
          typename
          guid
          FeedingPlanItems{
            typename
            guid
            percentage
            sequence
            startZhours
            tolerancePercentage
          }
        }
        Configuration(guid:$guid)
        KraalType(guid:$guid) {
          typename
          guid
          kraalType
          doFeed
          feedType
          noOfSessions
        }
        GenderType(guid:$guid) {
          typename
          guid
          genderType
          active
        }
        CribDeliverySequence(guid:$guid) {
          typename
          guid
          sequence{
            typename
            guid
            kraalId
            sequence
          }
        }
        DeliverySequence(guid:$guid) {
          typename
          guid
          sequence{
            typename
            guid
            kraalId
            sequence
          }
        }
        CribReadingAdjustments(guid:$guid) {
          typename
          guid
          useAliases
          useAdjustments
          callMoisture
          callonPreviousCall
          usePrecentageAdjustment
          useCallAdjustmentCalculation
          cribReadingAdjustment{
            typename
            guid
            score
            userAlias
            autoCallAdjustment
            call
          }
          
        }

      }`;
      let json = await this.$store.dispatch("graphQl", {
        gql,
        variables: { guid: this.$store.state.user.location.guid },
        url: "https://pi.gigalot.systems:7777/feeder",
      });

      console.log("Done Downloading");
      this.strategy = json.data.Strategy;
      this.kraalTypes = json.data.KraalType;
      this.genderTypes = json.data.GenderType;
      this.configuration = JSON.parse(json.data.Configuration);
      this.ensureConfigurationDefaults();
      this.cribDeliverySequence = json.data.CribDeliverySequence.sequence;
      this.driveDeliverySequence = json.data.DeliverySequence.sequence;
      this.sessionSelection = this.strategy.FeedingPlanItems.map((fpi) => fpi.sequence);
      this.cribAdjustments = json.data.CribReadingAdjustments;
      this.ensureCribAdjustmentDefaults();
    } catch (err) {
      console.log("getData -> error: " + err);
    } finally {
      this.loading = false;
    }
  }
  async getulr() {
    let url = `${this.$store.getters["backendUrl"]()}/resetCribOrder`;
    let jwt = await this.$store.dispatch("user/getOnlineIdToken", undefined, {
      root: true,
    });
    let res = await fetch(url, {
      method: "GET",
      headers: new Headers({ Authorization: `Bearer ${jwt}` }),
    });
    if (!res.ok) throw Error("Response OK : " + (await res.text()));
    await this.getData();
  }

  async getulrdrive() {
    let url = `${this.$store.getters["backendUrl"]()}/resetDriveOrder`;
    let jwt = await this.$store.dispatch("user/getOnlineIdToken", undefined, {
      root: true,
    });
    let res = await fetch(url, {
      method: "GET",
      headers: new Headers({ Authorization: `Bearer ${jwt}` }),
    });
    if (!res.ok) throw Error("Response OK : " + (await res.text()));
    await this.getData();
  }

  @Watch("webrtcEnabled")
  async onwebrtcEnabled(webrtcEnabled: boolean) {
    console.log(`Web rtc set to ${webrtcEnabled}`);
    this.$store.state.useP2PConn = webrtcEnabled;
  }

  addKraalType() {
    let kraalType: Models.KraalType = { typename: "KraalType", kraalType: "Name", guid: uuidv4(), doFeed: false, feedType: "Manual", noOfSessions: 1 };
    this.kraalTypes.push(kraalType);
    this.updateServerKraalType(JSON.stringify(kraalType));
  }

  createGenderType(name = ""): Models.GenderType {
    return { typename: "GenderType", genderType: name, guid: uuidv4(), active: true };
  }

  openAddGenderType() {
    this.genderDialogMode = "add";
    this.genderForm = this.createGenderType();
    this.genderDialog = true;
    this.resetGenderFormValidation();
  }

  openEditGenderType(item: Models.GenderType) {
    this.genderDialogMode = "edit";
    this.genderForm = { ...item };
    this.genderDialog = true;
    this.resetGenderFormValidation();
  }

  closeGenderDialog() {
    this.genderDialog = false;
  }

  resetGenderFormValidation() {
    this.$nextTick(() => {
      const form = this.$refs.genderForm as Vue & { resetValidation?: () => void };
      if (form && form.resetValidation) form.resetValidation();
    });
  }

  async saveGenderDialog() {
    const form = this.$refs.genderForm as Vue & { validate?: () => boolean };
    if (form && form.validate && !form.validate()) return;

    const genderType = String(this.genderForm.genderType || "").trim();
    if (!genderType) return;

    const duplicate = this.genderTypes.find(
      (item) => item.guid !== this.genderForm.guid && item.genderType.trim().toLowerCase() === genderType.toLowerCase()
    );
    if (duplicate) {
      this.snack = true;
      this.snackColor = "warning";
      this.snackText = "That gender type already exists";
      return;
    }

    const genderToSave: Models.GenderType = { ...this.genderForm, genderType };
    const existingIndex = this.genderTypes.findIndex((item) => item.guid === genderToSave.guid);
    if (existingIndex >= 0) {
      this.$set(this.genderTypes, existingIndex, genderToSave);
    } else {
      this.genderTypes.push(genderToSave);
    }

    await this.updateServerGenderType(genderToSave);
    this.closeGenderDialog();
  }

  confirmRemoveGenderType(item: Models.GenderType) {
    this.genderToRemove = item;
    this.removeGenderDialog = true;
  }

  closeRemoveGenderDialog() {
    this.removeGenderDialog = false;
    this.genderToRemove = null;
  }

  async removeGenderType() {
    if (!this.genderToRemove) return;

    const genderToSave: Models.GenderType = { ...this.genderToRemove, active: false };
    await this.saveGenderType(genderToSave);
    this.closeRemoveGenderDialog();
  }

  async restoreGenderType(item: Models.GenderType) {
    await this.saveGenderType({ ...item, active: true });
  }

  removeKraalType(guid: string) {}

  async putMergeSession() {
    try {
      console.log("updateServer()");
      let json = await this.$store.dispatch(
        "graphQl",
        {
          gql: `mutation mergeSessions($guid:String!, $user: String!) {
            mergeSessions(guid:$guid, user: $user) 
          }`,
          variables: { guid: this.$store.state.user.location.guid, user: JSON.stringify(this.$store.getters["user/getUpstreamMetadata"]()) },
        },
        { root: true }
      );

      this.snack = true;
      this.snackColor = "success";
      this.snackText = "Data saved";
    } catch (err) {
      console.log(err);
      this.errorDialog = true;
      this.errorText = `Error saving to server: ${err} `;

      throw err;
    }
  }

  // let url = `${this.$store.getters["backendUrl"]()}/mergeSessions`;
  // let jwt = await this.$store.dispatch("user/getOnlineIdToken", undefined, {
  //   root: true,
  // });
  // let body = JSON.stringify(this.$store.getters["user/getUpstreamMetadata"]());
  // console.log(body);
  // let res = await fetch(url, {
  //   method: "POST",
  //   mode: "cors",
  //   headers: new Headers({ Authorization: `Bearer ${jwt}`, "Content-Type": "application/json" }),
  //   body: body,
  // });

  // if (!res.ok) throw Error("Response OK : " + (await res.text()));
  // let data = await res.json();
  // console.log(data);
  openNewDayDialog(mode: "upload" | "noUpload") {
    this.newDayMode = mode;
    this.newDayDialog = true;
  }

  closeNewDayDialog() {
    this.newDayDialog = false;
    this.newDayMode = null;
  }

  async confirmNewDayAction() {
    const mode = this.newDayMode;
    this.newDayDialog = false;
    this.newDayMode = null;
    if (mode === "upload") {
      await this.newDay();
    } else if (mode === "noUpload") {
      await this.newDayNoUpload();
    }
  }

  async newDay() {
    try {
      let json = await this.$store.dispatch(
        "graphQl",
        {
          gql: `mutation newDay($guid: String!) {
            newDay(guid: $guid) 
            }`,
          variables: {
            guid: this.$store.state.user.location.guid,
          },
        },
        { root: true }
      );
    } catch (err) {
      console.log(err);
      this.errorDialog = true;
      this.errorText = `Error saving to server: ${err}`;
    }
  }

  async newDayNoUpload() {
    try {
      let json = await this.$store.dispatch(
        "graphQl",
        {
          gql: `mutation newDayNoUpload($guid: String!) {
            newDayNoUpload(guid: $guid) 
            }`,
          variables: {
            guid: this.$store.state.user.location.guid,
          },
        },
        { root: true }
      );
    } catch (err) {
      console.log(err);
      this.errorDialog = true;
      this.errorText = `Error saving to server: ${err}`;
    }
  }

  async saveConfig() {
    try {
      this.ensureConfigurationDefaults();
      let json = await this.$store.dispatch(
        "graphQl",
        {
          gql: `mutation changeConfiguration($guid: String!, $Configuration: String!) {
            changeConfiguration(guid: $guid, Configuration: $Configuration) 
            }`,
          variables: {
            guid: this.$store.state.user.location.guid,
            Configuration: JSON.stringify(this.configuration),
          },
        },
        { root: true }
      );

      this.snack = true;
      this.snackColor = "success";
      this.snackText = "Data saved";
    } catch (err) {
      console.log(err);
      this.errorDialog = true;
      this.errorText = `Error saving to server: ${err}`;
    }
  }

  async saveCribAdjustment(item: any) {
    if (item) {
      try {
        this.ensureCribAdjustmentDefaults();
        let json = await this.$store.dispatch(
          "graphQl",
          {
            gql: `mutation cribReadingAdjustments($guid: String!, $cribReadingAdjustments: CribReadingAdjustmentsInput!) {
            cribReadingAdjustments(guid: $guid, cribReadingAdjustments: $cribReadingAdjustments) 
            }`,
            variables: {
              guid: this.$store.state.user.location.guid,
              cribReadingAdjustments: this.cribAdjustments,
            },
          },
          { root: true }
        );

        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Data saved";
      } catch (err) {
        console.log(err);
        this.errorDialog = true;
        this.errorText = `Error saving to server: ${err}`;

        throw err;
      }
    }
  }

  async updateServerKraalType(object?: string) {
    console.log(object);
    if (object) {
      try {
        let json = await this.$store.dispatch(
          "graphQl",
          {
            gql: `mutation addKraalType($guid: String!, $kraalTypes: KraalTypeInput!) {
            addKraalType(guid: $guid, kraalTypes: $kraalTypes) 
            }`,
            variables: {
              guid: this.$store.state.user.location.guid,
              kraalTypes: object,
            },
          },
          { root: true }
        );

        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Data saved";
      } catch (err) {
        console.log(err);
        this.errorDialog = true;
        this.errorText = `Error saving to server: ${err}`;

        throw err;
      }
    }
  }

  async updateServerGenderType(object?: Models.GenderType) {
    console.log(object);
    if (object) {
      try {
        let json = await this.$store.dispatch(
          "graphQl",
          {
            gql: `mutation addGenderType($guid: String!, $genderType: GenderTypeInput!) {
            addGenderType(guid: $guid, genderType: $genderType) 
            }`,
            variables: {
              guid: this.$store.state.user.location.guid,
              genderType: object,
            },
          },
          { root: true }
        );

        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Data saved";
      } catch (err) {
        console.log(err);
        this.errorDialog = true;
        this.errorText = `Error saving to server: ${err}`;

        throw err;
      }
    }
  }

  moveStratUp() {
    if (this.selectedStrategy.length > 0) {
      let item = lodash.find(this.strategy.FeedingPlanItems, { guid: this.selectedStrategy[0].guid });
      if (item) {
        let nextItem = lodash.find(this.strategy.FeedingPlanItems, { sequence: item.sequence - 1 });
        item.sequence = item.sequence - 1;
        if (item.sequence < 0) {
          item.sequence = 0;
        }
        this.saveStrategy(item);
        //move item with sequence - 1 to below this one
        if (nextItem) {
          nextItem.sequence = nextItem.sequence + 1;
          this.saveStrategy(nextItem);
        }

        this.selectedStrategy = [];
        this.selectedStrategy.push(item);
      }
    }
  }

  moveStratDown() {
    if (this.selectedStrategy.length > 0) {
      let item = lodash.find(this.strategy.FeedingPlanItems, { guid: this.selectedStrategy[0].guid });
      if (item) {
        let nextItem = lodash.find(this.strategy.FeedingPlanItems, { sequence: item.sequence + 1 });
        item.sequence = item.sequence + 1;
        this.saveStrategy(item);
        //move item with sequence + 1 to above this one
        if (nextItem) {
          nextItem.sequence = nextItem.sequence - 1;
          if (nextItem.sequence < 0) {
            nextItem.sequence = 0;
          }
          this.saveStrategy(nextItem);
        }
        this.selectedStrategy = [];
        this.selectedStrategy.push(item);
      }
    }
  }

  moveCribDown() {
    if (this.selectedCrib.length > 0) {
      let item = lodash.find(this.cribDeliverySequence, { kraalId: this.selectedCrib[0].kraalId });
      if (item) {
        let nextItem = lodash.find(this.cribDeliverySequence, { sequence: item.sequence + 1 });
        item.sequence = item.sequence + 1;
        this.saveCribs(item);
        //move item with sequence + 1 to above this one
        if (nextItem) {
          nextItem.sequence = nextItem.sequence - 1;
          if (nextItem.sequence < 0) {
            nextItem.sequence = 0;
          }
          this.saveCribs(nextItem);
        }
        this.selectedCrib = [];
        this.selectedCrib.push(item);
      }
    }
  }

  moveCribUp() {
    if (this.selectedCrib.length > 0) {
      let item = lodash.find(this.cribDeliverySequence, { kraalId: this.selectedCrib[0].kraalId });
      if (item) {
        let nextItem = lodash.find(this.cribDeliverySequence, { sequence: item.sequence - 1 });
        item.sequence = item.sequence - 1;
        if (item.sequence < 0) {
          item.sequence = 0;
        }
        this.saveCribs(item);
        //move item with sequence - 1 to below this one
        if (nextItem) {
          nextItem.sequence = nextItem.sequence + 1;
          this.saveCribs(nextItem);
        }

        this.selectedCrib = [];
        this.selectedCrib.push(item);
      }
    }
  }

  moveDriveDown() {
    if (this.selectedDrive.length > 0) {
      let item = lodash.find(this.driveDeliverySequence, { kraalId: this.selectedDrive[0].kraalId });
      if (item) {
        let nextItem = lodash.find(this.driveDeliverySequence, { sequence: item.sequence + 1 });
        item.sequence = item.sequence + 1;
        this.saveDrive(item);
        //move item with sequence + 1 to above this one
        if (nextItem) {
          nextItem.sequence = nextItem.sequence - 1;
          if (nextItem.sequence < 0) {
            nextItem.sequence = 0;
          }
          this.saveDrive(nextItem);
        }
        this.selectedDrive = [];
        this.selectedDrive.push(item);
      }
    }
  }

  moveDriveUp() {
    if (this.selectedDrive.length > 0) {
      let item = lodash.find(this.driveDeliverySequence, { kraalId: this.selectedDrive[0].kraalId });
      if (item) {
        let nextItem = lodash.find(this.driveDeliverySequence, { sequence: item.sequence - 1 });
        item.sequence = item.sequence - 1;
        if (item.sequence < 0) {
          item.sequence = 0;
        }
        this.saveDrive(item);
        //move item with sequence - 1 to below this one
        if (nextItem) {
          nextItem.sequence = nextItem.sequence + 1;
          this.saveDrive(nextItem);
        }

        this.selectedDrive = [];
        this.selectedDrive.push(item);
      }
    }
  }

  saveKraalType(item: any) {
    this.updateServerKraalType(item);
  }

  async saveGenderType(item: Models.GenderType) {
    const index = this.genderTypes.findIndex((gender) => gender.guid === item.guid);
    if (index >= 0) this.$set(this.genderTypes, index, item);
    await this.updateServerGenderType(item);
  }

  save(item: any) {
    //update server with changes
    console.log("Save");
    this.updateServer(item);
  }

  addStrategyItem() {
    let item: Models.FeedingStrategyItem = {
      typename: "FeedingStrategyItem",
      guid: uuidv4(),
      sequence: this.strategy.FeedingPlanItems.length + 1,
      startZhours: "00:00",
      percentage: 0,
      tolerancePercentage: 0,
    };
    if (this.strategy) {
      this.strategy.FeedingPlanItems.push(item);
    }
  }

  removeStrategy(strategy: Models.FeedingStrategyItem[]) {
    if (strategy[0]) {
      lodash.remove(this.strategy.FeedingPlanItems, { guid: strategy[0].guid });
      this.updateServerStrategies(this.strategy);
      this.rerender();
    }
  }

  saveStrategy(item: any) {
    console.log("Save");
    this.updateServerStrategy(item);
  }

  rerender() {
    this.tableKey += 1;
  }

  saveCribs(item: any) {
    this.updateServerCribs(item);
  }

  saveDrive(item: any) {
    this.updateServerDrive(item);
  }

  //open_guid?: any = undefined;
  cancel() {
    console.log("Cancel");
    this.snack = true;
    this.snackColor = "blue lighten-1";
    this.snackText = "Canceled";
  }
  close() {
    console.log("Close");
  }
  async updateServerStrategies(object?: Models.FeedingStrategy) {
    console.log(object);
    if (object) {
      try {
        let json = await this.$store.dispatch(
          "graphQl",
          {
            gql: `mutation feedStrategy($guid: String!, $feedingStrategy: FeedingStrategyInput!) {
            feedStrategy(guid: $guid, feedingStrategy: $feedingStrategy) 
            }`,
            variables: {
              guid: this.$store.state.user.location.guid,
              feedingStrategy: object,
            },
          },
          { root: true }
        );

        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Data saved";
      } catch (err) {
        console.log(err);
        this.errorDialog = true;
        this.errorText = `Error saving to server: ${err}`;

        throw err;
      }
    }
  }

  async updateServerStrategy(object?: string) {
    console.log(object);
    if (object) {
      try {
        let json = await this.$store.dispatch(
          "graphQl",
          {
            gql: `mutation feedStrategyItem($guid: String!, $feedingStrategy: FeedingStrategyItemInput!) {
            feedStrategyItem(guid: $guid, feedingStrategy: $feedingStrategy) 
            }`,
            variables: {
              guid: this.$store.state.user.location.guid,
              feedingStrategy: object,
            },
          },
          { root: true }
        );

        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Data saved";
      } catch (err) {
        console.log(err);
        this.errorDialog = true;
        this.errorText = `Error saving to server: ${err}`;

        throw err;
      }
    }
  }

  async updateServer(object?: string) {
    console.log(object);
    if (object) {
      try {
        let json = await this.$store.dispatch(
          "graphQl",
          {
            gql: `mutation addRationPlan($guid: String!, $input: RationPlanItemInput!) {
            addRationPlan(guid: $guid, input: $input) 
            }`,
            variables: {
              guid: this.$store.state.user.location.guid,
              input: object,
            },
          },
          { root: true }
        );

        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Data saved";
      } catch (err) {
        console.log(err);
        this.errorDialog = true;
        this.errorText = `Error saving to server: ${err}`;

        throw err;
      }
    }
  }

  async updateServerCribs(object?: string) {
    console.log(object);
    if (object) {
      try {
        let json = await this.$store.dispatch(
          "graphQl",
          {
            gql: `mutation cribDeliverySequence($guid: String!, $cribdeliverySequence: CribDeliverySequenceItemInput!) {
            cribDeliverySequence(guid: $guid, cribdeliverySequence: $cribdeliverySequence) 
            }`,
            variables: {
              guid: this.$store.state.user.location.guid,
              cribdeliverySequence: object,
            },
          },
          { root: true }
        );

        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Data saved";
      } catch (err) {
        console.log(err);
        this.errorDialog = true;
        this.errorText = `Error saving to server: ${err}`;

        throw err;
      }
    }
  }

  async updateServerDrive(object?: string) {
    console.log(object);
    if (object) {
      try {
        let json = await this.$store.dispatch(
          "graphQl",
          {
            gql: `mutation deliverySequence($guid: String!, $deliverySequence: CribDeliverySequenceItemInput!) {
            deliverySequence(guid: $guid, deliverySequence: $deliverySequence) 
            }`,
            variables: {
              guid: this.$store.state.user.location.guid,
              deliverySequence: object,
            },
          },
          { root: true }
        );

        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Data saved";
      } catch (err) {
        console.log(err);
        this.errorDialog = true;
        this.errorText = `Error saving to server: ${err}`;

        throw err;
      }
    }
  }
}
